import { gql } from 'graphql-request'
import { useCallback } from 'react'
import useRequester from './use-requester'
import { useStores } from 'app/store'
import { useQuery } from 'react-query'

const hasWatchLaterBatchQuery = () => gql`
  query hasWatchLaterBatch($ids: [String]) {
    hasWatchLater(ids: $ids)
  }
`

interface UseWatchLaterBatchResult {
  watchLaterIds: string[]
  loading: boolean
  isInWatchLater: (id: string) => boolean
  refetch: () => Promise<void>
}

export const useWatchLaterBatch = (
  contentIds: string[]
): UseWatchLaterBatchResult => {
  const { request } = useRequester()
  const { userStore } = useStores()

  const { data, isLoading, refetch } = useQuery(
    ['watch-later-batch', contentIds],
    async () => {
      if (!userStore?.user?.id || contentIds.length === 0) {
        return []
      }

      const query = hasWatchLaterBatchQuery()
      const result = await request<{ hasWatchLater: string[] }>(query, {
        ids: contentIds,
      })
      return result?.hasWatchLater || []
    },
    {
      enabled: !!userStore?.user?.id && contentIds.length > 0,
      staleTime: 30000, // Consider data fresh for 30 seconds
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    }
  )

  const watchLaterIds = data || []

  const isInWatchLater = useCallback(
    (id: string) => {
      return watchLaterIds.includes(id)
    },
    [watchLaterIds],
  )

  return {
    watchLaterIds,
    loading: isLoading,
    isInWatchLater,
    refetch: async () => {
      await refetch()
    },
  }
}
